import React, { useEffect, useState } from 'react'
import {
  Box,
  ButtonBase,
  CircularProgress,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { Link, useNavigate } from 'react-router-dom'
import API from '../../../../services/API'
import KPI_SECTIONS from '../../../governance/kpi/sections'
import { statusColor } from '../../../governance/kpi/components/tileColors'

// Same thresholds as the KPI page's SectionScoreCard so the two views agree
const scoreStatus = (healthScore) => {
  if (healthScore === null || healthScore === undefined) return 'neutral'
  if (healthScore >= 80) return 'success'
  if (healthScore >= 60) return 'warning'
  return 'error'
}

const SectionRing = ({ section, summary }) => {
  const navigate = useNavigate()
  const colors = statusColor(scoreStatus(summary?.healthScore))
  const hasScore =
    summary?.healthScore !== null && summary?.healthScore !== undefined

  return (
    <ButtonBase
      onClick={() => navigate(`/kpi/${section.slug}`)}
      sx={{
        flex: '1 0 96px',
        minWidth: 96,
        borderRadius: 1,
        p: 1,
        flexDirection: 'column',
        height: '100%',
        gap: 0.75,
        '&:hover': { backgroundColor: 'anMuted.main' },
      }}
    >
      <Box position="relative" display="inline-flex">
        <CircularProgress
          variant="determinate"
          value={100}
          size={54}
          thickness={4}
          sx={{ color: 'greycontrast.main', position: 'absolute' }}
        />
        <CircularProgress
          variant="determinate"
          value={summary?.healthScore ?? 0}
          size={54}
          thickness={4}
          sx={{ color: colors.main }}
        />
        <Box
          sx={{
            inset: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ color: colors.main }}
          >
            {hasScore ? summary.healthScore : '—'}
          </Typography>
        </Box>
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          lineHeight: 1.2,
          textAlign: 'center',
          fontSize: '0.7rem',
        }}
      >
        {section.name}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: 'text.disabled', fontSize: '0.65rem', lineHeight: 1 }}
      >
        {summary?.rated
          ? `${summary.onTarget}/${summary.rated} on target`
          : 'No rated KPIs'}
      </Typography>
    </ButtonBase>
  )
}

// Headline health score for each KPI area, from the same /kpi-dashboard/overview
// data as the full KPI page (default range: last 30 days)
const KPIPulseCard = () => {
  const [sections, setSections] = useState(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    API.get('/kpi-dashboard/overview')
      .then((res) => {
        if (res.status === 200) setSections(res.data?.sections ?? [])
        else setError(true)
      })
      .catch((e) => {
        console.error(e)
        setError(true)
      })
  }, [])

  const summaries = Object.fromEntries(
    (sections ?? []).map((section) => [section.slug, section]),
  )

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Stack>
          <Typography variant="title">KPI health</Typography>
          <Typography variant="smallGrey">
            Overall score by area, last 30 days
          </Typography>
        </Stack>
        <Stack
          component={Link}
          to="/kpi"
          direction="row"
          alignItems="center"
          gap={0.5}
          sx={{
            textDecoration: 'none',
            color: 'contrast.main',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          <Typography variant="caption" fontWeight={600} color="inherit">
            All KPIs
          </Typography>
          <ArrowForwardIosIcon sx={{ fontSize: 11 }} />
        </Stack>
      </Stack>
      {error ? (
        <Typography variant="body2" mt={2}>
          KPI scores are unavailable right now.
        </Typography>
      ) : (
        // one row — scrolls sideways rather than wrapping when space is tight
        <Stack
          direction="row"
          gap={0.5}
          mt={1}
          sx={{ overflowX: 'auto', pb: 0.5 }}
        >
          {KPI_SECTIONS.map((section) =>
            sections ? (
              <SectionRing
                key={section.slug}
                section={section}
                summary={summaries[section.slug]}
              />
            ) : (
              <Stack
                key={section.slug}
                alignItems="center"
                p={1}
                gap={0.75}
                sx={{ flex: '1 0 96px', minWidth: 96 }}
              >
                <Skeleton variant="circular" width={54} height={54} />
                <Skeleton variant="text" width="70%" />
              </Stack>
            ),
          )}
        </Stack>
      )}
    </Paper>
  )
}

export default KPIPulseCard
